import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useShop } from '../../context/ShopContext';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
  Divider,
  Breadcrumbs,
  useTheme as useMuiTheme,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
} from '@mui/material';
import Button from '@mui/material/Button';
import FarmIcon from '@mui/icons-material/Agriculture';
import CalculateIcon from '@mui/icons-material/Calculate';
import StoreIcon from '@mui/icons-material/Store';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocalShipping from '@mui/icons-material/LocalShipping';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import NatureIcon from '@mui/icons-material/Nature';
import ForestIcon from '@mui/icons-material/Forest';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ForumIcon from '@mui/icons-material/Forum';
import GlobalIcon from '@mui/icons-material/Public';
import GroupIcon from '@mui/icons-material/Group';
import CampaignIcon from '@mui/icons-material/Campaign';
import InsightsIcon from '@mui/icons-material/Insights';
import DealIcon from '@mui/icons-material/Handshake';
import ThemeToggle from '../common/ThemeToggle';
import { useTheme } from '../../theme/ThemeContext';
import { useDashboardNotifications } from '../../context/DashboardNotificationContext';
import { useUser } from '../../context/UserContext';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CircleIcon from '@mui/icons-material/Circle';
import SubscriptionIcon from '@mui/icons-material/CreditCard';
import AdminIcon from '@mui/icons-material/AdminPanelSettings';

const drawerWidth = 212; // Match the dashboard's navbar width

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { activeShop } = useShop();
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const { hasModule } = useUser();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const profileMenuOpen = Boolean(profileMenuAnchorEl);
  const [notificationMenuAnchorEl, setNotificationMenuAnchorEl] = useState<null | HTMLElement>(null);
  const notificationMenuOpen = Boolean(notificationMenuAnchorEl);
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useDashboardNotifications();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchorEl(event.currentTarget);
  };
  const handleProfileMenuClose = () => {
    setProfileMenuAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationMenuAnchorEl(event.currentTarget);
  };
  const handleNotificationMenuClose = () => {
    setNotificationMenuAnchorEl(null);
  };

  const handleNotificationClick = (notification: any) => {
    markAsRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
    }
    handleNotificationMenuClose();
  };

  // Generate breadcrumbs from current location
  const pathnames = location.pathname.split('/').filter((x) => x);
  const breadcrumbNameMap: { [key: string]: string } = {
    dashboard: 'Dashboard',
    'my-farms': 'My Farms',
    'my-shops': 'My Shops',
    calculator: 'Farm Calculator',
    marketplace: 'Marketplace',
    payment: 'Payments',
    logistics: 'Logistics',
    community: 'Community',
    profile: 'Profile',
    settings: 'Settings',
    'smart-farming': 'Smart Farming',
    'carbon-credits': 'Carbon Credits',
    'products': 'Manage Products',
    'orders': 'Seller Orders',
  };

  const renderBreadcrumbs = () => {
    if (pathnames.length === 0) return null;

    return (
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ flexGrow: 1, color: 'white' }}
      >
        {pathnames.map((value, index) => {
          const last = index === pathnames.length - 1;
          const to = `/${pathnames.slice(0, index + 1).join('/')}`;
          
          // Hide UUIDs/Database IDs from breadcrumbs and use human-readable names
          const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
          if (isUuid) return null; // Skip IDs in breadcrumbs

          const displayName = breadcrumbNameMap[value] || value.charAt(0).toUpperCase() + value.slice(1);

          return last ? (
            <Typography color="white" key={to}>
              {displayName}
            </Typography>
          ) : (
            <Link
              color="inherit"
              to={to}
              key={to}
              style={{
                color: 'white',
                textDecoration: 'none',
              }}
            >
              {displayName}
            </Link>
          );
        })}
      </Breadcrumbs>
    );
  };

  const drawer = (
    <div>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              bgcolor: 'action.selected',
              borderRadius: '80px',
              overflow: 'hidden'
            }}
          />
          <Typography
            variant="body1"
            component={Link}
            to="/"
            sx={{
              textDecoration: 'none',
              color: 'text.primary',
              fontWeight: 400,
            }}
          >
            Farmers HUB
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="body2"
            sx={{
              px: 2,
              py: 1,
              color: 'text.secondary',
            }}
          >
            Menu
          </Typography>
        </Box>
      </Box>
      <List sx={{ px: 1 }}>
        {/* Dashboard link - uses info color for highlight when selected */}
        <ListItemButton
          onClick={() => navigate('/dashboard')}
          selected={location.pathname === '/dashboard'}
          sx={{
            borderRadius: '12px',
            mb: 1,
            '&.Mui-selected': {
              backgroundColor: 'info.main', // Use info color for selected
              color: 'white',
            },
            '&:hover': {
              backgroundColor: 'info.light', // Lighter info color on hover
              color: 'white',
            },
          }}
          aria-label="Go to Dashboard"
        >
          <ListItemIcon sx={{ color: location.pathname === '/dashboard' ? 'white' : 'text.primary', minWidth: 40 }}>
            <DashboardIcon sx={{ width: 20, height: 20 }} />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItemButton>

        {/* Farm Management Section */}
        {hasModule('farm_management') && (
          <>
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>Farm Management</Typography>
            </Box>
            <ListItemButton
              onClick={() => navigate('/dashboard/calculator')}
              selected={location.pathname === '/dashboard/calculator'}
              sx={{
                borderRadius: '12px',
                mb: 1,
                '&.Mui-selected': { backgroundColor: 'success.main', color: 'white' },
                '&:hover': { backgroundColor: 'success.light', color: 'white' },
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === '/dashboard/calculator' ? 'white' : 'text.primary', minWidth: 40 }}>
                <CalculateIcon sx={{ width: 20, height: 20 }} />
              </ListItemIcon>
              <ListItemText primary="Farm Calculator" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
            </ListItemButton>
            <ListItemButton
              onClick={() => navigate('/dashboard/my-farms')}
              selected={location.pathname === '/dashboard/my-farms'}
              sx={{
                borderRadius: '12px',
                mb: 1,
                '&.Mui-selected': { backgroundColor: 'primary.main', color: 'white' },
                '&:hover': { backgroundColor: 'primary.light', color: 'white' },
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === '/dashboard/my-farms' ? 'white' : 'text.primary', minWidth: 40 }}>
                <FarmIcon sx={{ width: 20, height: 20 }} />
              </ListItemIcon>
              <ListItemText primary="My Farms" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
            </ListItemButton>
          </>
        )}

        {/* Marketplace Seller Section */}
        {hasModule('marketplace_seller') && (
          <>
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>Seller Hub</Typography>
            </Box>
            <ListItemButton
              onClick={() => navigate('/dashboard/my-shops')}
              selected={location.pathname === '/dashboard/my-shops'}
              sx={{
                borderRadius: '12px',
                mb: 1,
                '&.Mui-selected': { backgroundColor: 'warning.main', color: 'white' },
                '&:hover': { backgroundColor: 'warning.light', color: 'white' },
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === '/dashboard/my-shops' ? 'white' : 'text.primary', minWidth: 40 }}>
                <StoreIcon sx={{ width: 20, height: 20 }} />
              </ListItemIcon>
              <ListItemText primary="My Shops" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
            </ListItemButton>
            {activeShop && (
              <>
                <ListItemButton
                  onClick={() => navigate(`/dashboard/my-shops/${activeShop.id}/wealth`)}
                  selected={location.pathname === `/dashboard/my-shops/${activeShop.id}/wealth`}
                  sx={{
                    borderRadius: '12px',
                    mb: 1,
                    '&.Mui-selected': { backgroundColor: 'success.main', color: 'white' },
                    '&:hover': { backgroundColor: 'success.light', color: 'white' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <InsightsIcon sx={{ width: 20, height: 20, color: location.pathname.includes('/wealth') ? 'white' : 'text.primary' }} />
                  </ListItemIcon>
                  <ListItemText primary="Wealth Center" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => navigate(`/dashboard/my-shops/${activeShop.id}/deals`)}
                  selected={location.pathname === `/dashboard/my-shops/${activeShop.id}/deals`}
                  sx={{
                    borderRadius: '12px',
                    mb: 1,
                    '&.Mui-selected': { backgroundColor: 'primary.main', color: 'white' },
                    '&:hover': { backgroundColor: 'primary.light', color: 'white' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <DealIcon sx={{ width: 20, height: 20, color: location.pathname.includes('/deals') ? 'white' : 'text.primary' }} />
                  </ListItemIcon>
                  <ListItemText primary="Deal Room (B2B)" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => navigate('/dashboard/products')}
                  selected={location.pathname === '/dashboard/products'}
                  sx={{
                    borderRadius: '12px',
                    mb: 1,
                    '&.Mui-selected': { backgroundColor: 'warning.main', color: 'white' },
                    '&:hover': { backgroundColor: 'warning.light', color: 'white' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <InventoryIcon sx={{ width: 20, height: 20, color: location.pathname === '/dashboard/products' ? 'white' : 'text.primary' }} />
                  </ListItemIcon>
                  <ListItemText primary="Manage Products" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
                </ListItemButton>
              </>
            )}
          </>
        )}

        {/* Global Trade & Carbon Section */}
        {(hasModule('marketplace_seller') || hasModule('carbon_credits')) && (
          <>
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>Global Features</Typography>
            </Box>
            {hasModule('marketplace_seller') && (
              <ListItemButton
                onClick={() => navigate('/dashboard/import-export')}
                selected={location.pathname === '/dashboard/import-export'}
                sx={{
                  borderRadius: '12px',
                  mb: 1,
                  '&.Mui-selected': { backgroundColor: 'info.main', color: 'white' },
                  '&:hover': { backgroundColor: 'info.light', color: 'white' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <GlobalIcon sx={{ width: 20, height: 20, color: location.pathname === '/dashboard/import-export' ? 'white' : 'text.primary' }} />
                </ListItemIcon>
                <ListItemText primary="Global Trade Intel" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
              </ListItemButton>
            )}
            {hasModule('carbon_credits') && (
              <ListItemButton
                onClick={() => navigate('/dashboard/carbon-credits')}
                selected={location.pathname === '/dashboard/carbon-credits'}
                sx={{
                  borderRadius: '12px',
                  mb: 1,
                  '&.Mui-selected': { backgroundColor: 'success.main', color: 'white' },
                  '&:hover': { backgroundColor: 'success.light', color: 'white' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ForestIcon sx={{ width: 20, height: 20, color: location.pathname === '/dashboard/carbon-credits' ? 'white' : 'text.primary' }} />
                </ListItemIcon>
                <ListItemText primary="Carbon Credits" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
              </ListItemButton>
            )}
          </>
        )}

        {/* Marketplace Buyer Section */}
        {hasModule('marketplace_buyer') && (
          <>
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>Marketplace</Typography>
            </Box>
            <ListItemButton
              onClick={() => navigate('/dashboard/marketplace')}
              selected={location.pathname === '/dashboard/marketplace'}
              sx={{
                borderRadius: '12px',
                mb: 1,
                '&.Mui-selected': { backgroundColor: 'secondary.main', color: 'white' },
                '&:hover': { backgroundColor: 'secondary.light', color: 'white' },
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === '/dashboard/marketplace' ? 'white' : 'text.primary', minWidth: 40 }}>
                <StoreIcon sx={{ width: 20, height: 20 }} />
              </ListItemIcon>
              <ListItemText primary="Marketplace" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
            </ListItemButton>
          </>
        )}

        {/* Logistics Section */}
        {hasModule('logistics') && (
          <>
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>Supply Chain</Typography>
            </Box>
            <ListItemButton
              onClick={() => navigate('/dashboard/logistics')}
              selected={location.pathname === '/dashboard/logistics'}
              sx={{
                borderRadius: '12px',
                mb: 1,
                '&.Mui-selected': { backgroundColor: 'info.main', color: 'white' },
                '&:hover': { backgroundColor: 'info.light', color: 'white' },
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === '/dashboard/logistics' ? 'white' : 'text.primary', minWidth: 40 }}>
                <LocalShipping sx={{ width: 20, height: 20 }} />
              </ListItemIcon>
              <ListItemText primary="Logistics" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
            </ListItemButton>
          </>
        )}

        {/* Community & Settings */}
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="body2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>More</Typography>
        </Box>
        <ListItemButton
          onClick={() => navigate('/dashboard/community')}
          selected={location.pathname === '/dashboard/community'}
          sx={{
            borderRadius: '12px',
            mb: 1,
            '&.Mui-selected': { backgroundColor: 'primary.main', color: 'white' },
            '&:hover': { backgroundColor: 'primary.light', color: 'white' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <ForumIcon sx={{ width: 20, height: 20, color: location.pathname === '/dashboard/community' ? 'white' : 'text.primary' }} />
          </ListItemIcon>
          <ListItemText primary="Community Hub" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
        </ListItemButton>
        <ListItemButton
          onClick={() => navigate('/account')}
          selected={location.pathname === '/account'}
          sx={{
            borderRadius: '12px',
            mb: 1,
            '&.Mui-selected': { backgroundColor: 'neutral.500', color: 'white' },
            '&:hover': { backgroundColor: 'neutral.500', color: 'white', opacity: 0.85 },
          }}
        >
          <ListItemIcon sx={{ color: location.pathname === '/account' ? 'white' : 'text.primary', minWidth: 40 }}>
            <PersonIcon sx={{ width: 20, height: 20 }} />
          </ListItemIcon>
          <ListItemText primary="My Account" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
        </ListItemButton>
        <ListItemButton
          onClick={() => navigate('/dashboard/subscriptions')}
          selected={location.pathname === '/dashboard/subscriptions'}
          sx={{
            borderRadius: '12px',
            mb: 1,
            '&.Mui-selected': { backgroundColor: 'neutral.500', color: 'white' },
            '&:hover': { backgroundColor: 'neutral.500', color: 'white', opacity: 0.85 },
          }}
        >
          <ListItemIcon sx={{ color: location.pathname === '/dashboard/subscriptions' ? 'white' : 'text.primary', minWidth: 40 }}>
            <SubscriptionIcon sx={{ width: 20, height: 20 }} />
          </ListItemIcon>
          <ListItemText primary="Subscription" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
        </ListItemButton>

        {hasModule('admin') && (
          <ListItemButton
            onClick={() => navigate('/dashboard/admin')}
            selected={location.pathname === '/dashboard/admin'}
            sx={{
              borderRadius: '12px',
              mb: 1,
              '&.Mui-selected': { backgroundColor: 'error.dark', color: 'white' },
              '&:hover': { backgroundColor: 'error.main', color: 'white' },
            }}
          >
            <ListItemIcon sx={{ color: location.pathname === '/dashboard/admin' ? 'white' : 'text.primary', minWidth: 40 }}>
              <AdminIcon sx={{ width: 20, height: 20 }} />
            </ListItemIcon>
            <ListItemText primary="Admin Portal" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
          </ListItemButton>
        )}
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: '12px',
            mb: 1,
            '&:hover': { backgroundColor: 'error.main', color: 'white' },
          }}
        >
          <ListItemIcon sx={{ color: 'error.main', minWidth: 40 }}>
            <LogoutIcon sx={{ width: 20, height: 20 }} />
          </ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ sx: { fontSize: '14px', fontWeight: 400 } }} />
        </ListItemButton>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'primary.main',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Home button */}
          <IconButton
            color="inherit"
            aria-label="go to home page"
            component={Link}
            to="/"
            sx={{ mr: 2 }}
          >
            <DashboardIcon sx={{ mr: 0.5 }} />
            <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>
              Home
            </Typography>
          </IconButton>

          {renderBreadcrumbs()}

          {/* Theme toggle */}
          <ThemeToggle />

          {/* Notifications Bell */}
          <IconButton
            size="large"
            color="inherit"
            onClick={handleNotificationMenuOpen}
            sx={{ ml: 1 }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Menu
            anchorEl={notificationMenuAnchorEl}
            open={notificationMenuOpen}
            onClose={handleNotificationMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                width: 320,
                maxHeight: 400,
                borderRadius: '12px',
                boxShadow: 4,
              }
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold">Notifications</Typography>
              <Button size="small" onClick={markAllAsRead}>Mark all read</Button>
            </Box>
            <Divider />
            <List sx={{ p: 0 }}>
              {notifications.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No new notifications</Typography>
                </Box>
              ) : (
                notifications.map((notif) => (
                  <MenuItem 
                    key={notif.id} 
                    onClick={() => handleNotificationClick(notif)}
                    sx={{ 
                      py: 1.5, 
                      px: 2, 
                      borderBottom: '1px solid', 
                      borderColor: 'divider',
                      bgcolor: notif.read ? 'transparent' : 'action.hover',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      whiteSpace: 'normal'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, width: '100%' }}>
                      {!notif.read && <CircleIcon sx={{ fontSize: 10, color: 'primary.main' }} />}
                      <Typography variant="subtitle2" fontWeight={notif.read ? 500 : 700} sx={{ flexGrow: 1 }}>
                        {notif.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {notif.message}
                    </Typography>
                  </MenuItem>
                ))
              )}
            </List>
            {notifications.length > 0 && (
              <Box sx={{ p: 1, textAlign: 'center' }}>
                <Button fullWidth size="small" component={Link} to="/dashboard/notifications" onClick={handleNotificationMenuClose}>
                  View All Notifications
                </Button>
              </Box>
            )}
          </Menu>

          {/* Profile menu */}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="profile-menu"
            aria-haspopup="true"
            color="inherit"
            onClick={handleProfileMenuOpen}
          >
            <AccountCircle />
          </IconButton>
          <Menu
            id="profile-menu"
            anchorEl={profileMenuAnchorEl}
            open={profileMenuOpen}
            onClose={handleProfileMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 180,
                bgcolor: 'background.paper',
                color: isDarkMode ? 'text.secondary' : 'text.primary',
                boxShadow: 3,
                borderRadius: 1,
              },
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem component={Link} to="/account" onClick={handleProfileMenuClose}>
              <ListItemIcon><PersonIcon fontSize="small" sx={{ color: 'inherit' }} /></ListItemIcon>
              My Account
            </MenuItem>
            <MenuItem component={Link} to="/dashboard/profile" onClick={handleProfileMenuClose}>
              <ListItemIcon><SettingsIcon fontSize="small" sx={{ color: 'inherit' }} /></ListItemIcon>
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { handleProfileMenuClose(); handleLogout(); }}>
              <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer for larger screens */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          },
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Drawer for mobile screens */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        key={location.pathname} // Force re-mount on navigation to fix 'blank page' issues and ensure fresh data
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          mt: '64px', // Height of AppBar
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default DashboardLayout;
